<script setup lang="ts">
    import type { AcademyFilter, AcademyFilterState } from '~/types/academy/filters';
    import { isHiddenFilterCode } from '~/utils/academy/constants';

    const { pending, error, filters, filterState } = defineProps<{
        pending: boolean;
        error: string | null;
        filters: AcademyFilter[];
        filterState: AcademyFilterState;
    }>();

    defineEmits<{
        'toggle-option': [filterCode: string, optionKey: string];
    }>();

    const visibleFilters = computed(() => filters.filter((filter) => !isHiddenFilterCode(filter.code)));

    /** The panel reads this value only when it mounts. So a filter click does not open or close a panel. */
    const hasSelection = (filterCode: string): boolean => (filterState[filterCode]?.selectedKeys.length ?? 0) > 0;
</script>

<template>
    <div
        v-if="pending && visibleFilters.length === 0"
        class="loading"
    >
        <LoadingSpinner />
    </div>

    <pre
        v-else-if="error"
        class="error"
    >
        {{ error }}
    </pre>

    <div
        v-else-if="visibleFilters.length"
        class="sections"
    >
        <ExpandablePanel
            v-for="filter in visibleFilters"
            :key="filter.code"
            :title="
                $te(`academy.trainings.filter.title.${filter.title}`)
                    ? $t(`academy.trainings.filter.title.${filter.title}`)
                    : filter.title
            "
            :panel-key="filter.code"
            :initial-open="hasSelection(filter.code)"
            class="filter-group"
        >
            <AcademyTrainingsListingFilterInput
                :filter="filter"
                :state="filterState[filter.code]"
                @toggle-option="$emit('toggle-option', filter.code, $event)"
            />
        </ExpandablePanel>
    </div>
</template>

<style lang="scss" scoped>
    .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 8rem;
    }

    .error {
        margin: 0;
        white-space: pre-wrap;
        word-break: break-word;
        overflow: auto;
    }

    .sections {
        display: flex;
        flex-direction: column;
        gap: var(--spacing);

        .filter-group {
            background-color: var(--white);
            border-radius: var(--border-radius-s);
        }
    }
</style>
